import { useEffect, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RiSearchLine, RiImageLine, RiStackLine } from "@remixicon/react";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { StockMovementDialog } from "./StockMovementDialog";

export type StockStatus = "all" | "in_stock" | "low_stock" | "out_of_stock";

export type InventoryItem = {
  productId: string;
  variantId: string;
  name: string;
  sku?: string;
  barcode?: string;
  variantLabel?: string;
  inventory: number;
  lowStockThreshold: number;
  stockStatus: Exclude<StockStatus, "all">;
  price?: number;
  image?: string | null;
  categoryName?: string;
  status: string;
};

type InventoryResponse = {
  items: InventoryItem[];
  summary: {
    totalSkus: number;
    totalUnits: number;
    lowStock: number;
    outOfStock: number;
    inStock: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
};

const STOCK_FILTERS: Array<{ value: StockStatus; label: string }> = [
  { value: "all", label: "All" },
  { value: "out_of_stock", label: "Out of stock" },
  { value: "low_stock", label: "Low stock" },
  { value: "in_stock", label: "In stock" },
];

function Highlight({ text, query }: { text?: string | null; query: string }) {
  if (!text) return null;
  const term = query.trim();
  if (!term) return <>{text}</>;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "ig"));
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === term.toLowerCase() ? (
          <mark key={`${part}-${index}`} className="rounded-sm bg-primary/15 px-0.5 text-foreground">
            {part}
          </mark>
        ) : (
          <span key={`${part}-${index}`}>{part}</span>
        ),
      )}
    </>
  );
}

function StockBadge({ status, quantity }: { status: string; quantity: number }) {
  if (status === "in_stock") {
    return (
      <Badge variant="secondary" className="rounded-full border-0 bg-emerald-50 text-emerald-700">
        In stock ({quantity})
      </Badge>
    );
  }
  if (status === "low_stock") {
    return (
      <Badge variant="secondary" className="rounded-full border-0 bg-amber-50 text-amber-700">
        Low stock ({quantity})
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="rounded-full border-0 bg-red-50 text-red-700">
      Out of stock
    </Badge>
  );
}

export function InventoryList({
  onSummaryChange,
}: {
  onSummaryChange?: (summary: InventoryResponse["summary"] | undefined, loading: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { storeId } = useCurrentStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const stockStatus = (searchParams.get("stockStatus") as StockStatus) || "all";

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 200);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [stockStatus]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const setStockStatus = (value: StockStatus) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("stockStatus");
    else next.set("stockStatus", value);
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const queryKey = ["inventory", storeId, search, category, stockStatus, page];

  const { data, isLoading, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (category !== "all") params.append("category", category);
      if (stockStatus !== "all") params.append("stockStatus", stockStatus);
      params.append("page", String(page));
      params.append("limit", "25");
      const { data: response } = await api.get<InventoryResponse>(`/api/inventory?${params}`);
      return response;
    },
    enabled: !!storeId,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    onSummaryChange?.(data?.summary, isLoading && !data);
  }, [data?.summary, isLoading, data, onSummaryChange]);

  const { data: categories = [] } = useQuery({
    queryKey: ["categories", storeId],
    queryFn: async () => {
      const { data: response } = await api.get("/api/categories?tree=true");
      return response;
    },
    enabled: !!storeId,
  });

  const flattenCategories = (
    cats: Array<{ _id: string; name: string; children?: unknown[] }>,
  ): Array<{ _id: string; name: string; level: number }> => {
    const result: Array<{ _id: string; name: string; level: number }> = [];
    const flatten = (nodes: Array<{ _id: string; name: string; children?: unknown[] }>, level = 0) => {
      nodes.forEach((cat) => {
        result.push({ _id: cat._id, name: cat.name, level });
        if (Array.isArray(cat.children) && cat.children.length > 0) {
          flatten(cat.children as Array<{ _id: string; name: string; children?: unknown[] }>, level + 1);
        }
      });
    };
    flatten(cats);
    return result;
  };

  const items = data?.items || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };

  const handleSaved = () => {
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["inventory-movements"] });
    queryClient.invalidateQueries({ queryKey: ["products"] });
    queryClient.invalidateQueries({ queryKey: ["products-stats"] });
  };

  return (
    <SectionCard>
      <div className="mb-4 flex flex-col gap-4">
        <div className="flex flex-col gap-3 lg:flex-row">
          <div className="relative min-w-0 flex-1">
            <RiSearchLine className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, SKU, or barcode"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className={dashboardStyles.filterInput}
              autoFocus
            />
          </div>
          <Select
            value={category}
            onValueChange={(value) => {
              setCategory(value);
              setPage(1);
            }}
          >
            <SelectTrigger className={`${dashboardStyles.filterSelect} w-full min-w-[140px] max-w-[220px] sm:w-auto`}>
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {flattenCategories(Array.isArray(categories) ? categories : []).map((cat) => (
                <SelectItem key={cat._id} value={cat._id}>
                  {"— ".repeat(cat.level)}
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-2">
          {STOCK_FILTERS.map((filter) => {
            const active = stockStatus === filter.value;
            return (
              <Button
                key={filter.value}
                type="button"
                variant="ghost"
                onClick={() => setStockStatus(filter.value)}
                className={cn(
                  "h-9 rounded-full px-3.5 text-xs font-semibold shadow-none",
                  active
                    ? "bg-[#EDE4FB] text-primary hover:bg-[#EDE4FB] hover:text-primary"
                    : "bg-[#F5F5F3] text-muted-foreground hover:bg-[#EFEFEA] hover:text-foreground",
                )}
              >
                {filter.label}
              </Button>
            );
          })}
        </div>
      </div>

      {isLoading && !data ? (
        isMobile ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className={dashboardStyles.listItem}>
                <Skeleton className="h-14 w-14 shrink-0 rounded-lg" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={dashboardStyles.tableWrap}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>On hand</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 8 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-11 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-24 rounded-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-28 rounded-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl bg-muted/20 px-4 py-16">
          <div className={dashboardStyles.emptyIcon}>
            <RiStackLine className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className={dashboardStyles.emptyTitle}>
            {search || category !== "all" || stockStatus !== "all" ? "No matching stock" : "No inventory yet"}
          </p>
          <p className={dashboardStyles.emptyDescription}>
            {search || category !== "all" || stockStatus !== "all"
              ? "Try a different name, SKU, or barcode."
              : "Add products to start tracking stock."}
          </p>
        </div>
      ) : isMobile ? (
        <div className={cn("space-y-3", isFetching && "opacity-80")}>
          {items.map((item) => (
            <div key={item.variantId} className={dashboardStyles.listItem}>
              <div className="flex w-full items-start gap-3">
                {item.image ? (
                  <img src={item.image} alt={item.name} className="h-14 w-14 shrink-0 rounded-lg border object-cover" />
                ) : (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border bg-gray-100">
                    <RiImageLine className="h-5 w-5 text-gray-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1 space-y-2">
                  <Link
                    to={`/dashboard/products/${item.productId}`}
                    className="block truncate font-medium text-foreground hover:text-primary"
                  >
                    <Highlight text={item.name} query={search} />
                  </Link>
                  <p className="truncate text-xs text-muted-foreground">
                    {item.variantLabel || "Default"}
                    {item.sku ? (
                      <>
                        {" · "}
                        <Highlight text={item.sku} query={search} />
                      </>
                    ) : null}
                  </p>
                  <div className="flex items-center justify-between gap-2">
                    <StockBadge status={item.stockStatus} quantity={item.inventory} />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 rounded-full px-3 text-xs font-semibold"
                      onClick={() => setSelectedItem(item)}
                    >
                      Update
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={cn(dashboardStyles.tableWrap, isFetching && "opacity-80")}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">On hand</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.variantId}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="h-11 w-11 shrink-0 rounded-lg border object-cover" />
                      ) : (
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border bg-gray-100">
                          <RiImageLine className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <Link
                          to={`/dashboard/products/${item.productId}`}
                          className="block truncate font-medium text-foreground hover:text-primary"
                        >
                          <Highlight text={item.name} query={search} />
                        </Link>
                        <p className="truncate text-xs text-muted-foreground">
                          {item.variantLabel || "Default variant"}
                          {item.categoryName ? ` · ${item.categoryName}` : ""}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    <Highlight text={item.sku || "—"} query={search} />
                  </TableCell>
                  <TableCell>
                    <StockBadge status={item.stockStatus} quantity={item.inventory} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="tabular-nums text-sm font-semibold">{item.inventory}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 rounded-full px-3 text-xs font-semibold"
                        onClick={() => setSelectedItem(item)}
                      >
                        Update
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {pagination.pages > 1 ? (
        <div className={dashboardStyles.paginationBar}>
          <p>
            Showing page {pagination.page} of {pagination.pages} ({pagination.total} SKUs)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className={dashboardStyles.paginationBtn}
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={dashboardStyles.paginationBtn}
              onClick={() => setPage(page + 1)}
              disabled={page === pagination.pages}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <StockMovementDialog
        item={selectedItem}
        open={!!selectedItem}
        onOpenChange={(open) => {
          if (!open) setSelectedItem(null);
        }}
        onSaved={handleSaved}
      />
    </SectionCard>
  );
}
